import React, { useEffect, useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import {
  TableContainer,
  Table,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { isArray, isEmpty, isFunction } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { Modal } from '~/components';
import { t } from '~/helpers';
import api from '~/services/api';

import Confirmation from './components/Remove';
import * as S from './styles';

const SimpleTable = ({
  heading,
  url,
  onCreate,
  onRead,
  onUpdate,
  onDelete,
  cells,
  unique,
  form: Form,
  title,
  restProps,
  query,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [active, setActive] = useState({});

  const modalRef = React.useRef(null);
  const deleteRef = React.useRef(null);

  // GET data
  const handleRequestGETData = useCallback(async (param = {}) => {
    const params = { ...param, ...query };

    setLoading(true);
    setActive({});

    const response = await api.get(url, {
      params,
      paramsSerializer: (q) => qs.stringify(q),
    });

    setLoading(false);

    if (isArray(response.data.items)) {
      setData(response.data.items);
    } else {
      setData(response.data);
    }
  }, []);

  // serialize result in cell
  const handleSerializeResult = useCallback((item, cell) => {
    const { id, converter, format } = cell;

    if (converter) {
      return converter.find((conv) => conv.label === item[id]).value;
    }

    if (isFunction(format)) {
      return format(item[id]);
    }

    return item[id];
  }, []);

  // click in cell table
  const handleSetActiveItem = useCallback((item) => setActive(item), []);

  // Open modal
  const openModal = useCallback(
    (params = {}) => {
      // loading
      const load = {
        loading,
        toggleLoading: (value) => setLoading(value),
      };

      modalRef.current.handleOpenModal({
        ...params,
        load,
        restProps,
        rendering: data,
      });
    },
    [data]
  );

  // Confirmation delete register
  const confirmationDelete = useCallback(() => {
    deleteRef.current.handleOpenModal();
  }, []);

  // Cancell delete
  const cancellDelete = useCallback(() => {
    deleteRef.current.handleCloseModal();
  });

  // Confirme delete
  const confirmDelete = useCallback(async () => {
    const paramsURL = [];

    setLoading(true);

    await unique.map((un) => paramsURL.push(active[un]));

    try {
      await api.delete(url.concat('/', paramsURL.join('/')));
      await handleRequestGETData();

      cancellDelete();
      toast.success(t('s.geral.sucessoexclusao'));
    } catch (err) {
      err.map((e) => toast.error(e));
    } finally {
      setLoading(false);
    }
  }, [active]);

  // read data initial
  useEffect(() => {
    if (onRead) {
      handleRequestGETData();
    }
  }, []);

  return (
    <>
      <S.Wrapper loading={loading}>
        {loading && <CircularProgress className="loadingTable" />}

        <S.Header>
          {!!heading && heading}

          {onCreate && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => openModal()}
            >
              {t('s.geral.botaoincluir')}
            </Button>
          )}

          {!isEmpty(active) && onUpdate && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => openModal(active)}
            >
              {t('s.geral.botaoalterar')}
            </Button>
          )}

          {!isEmpty(active) && onDelete && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmationDelete}
            >
              {t('s.geral.botaoexcluir')}
            </Button>
          )}
        </S.Header>
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                {cells.map((cell) => (
                  <TableCell key={cell.id}>{cell.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!!data &&
                data.map((item) => (
                  <TableRow
                    key={item[unique[0]]}
                    onClick={() => handleSetActiveItem(item)}
                    selected={!!active.rownum && active.rownum === item.rownum}
                  >
                    {cells.map((cell) => (
                      <TableCell
                        key={`cell-${item.rownum}-${cell.id}`}
                        align={cell.align || 'inherit'}
                      >
                        {handleSerializeResult(item, cell)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </S.Wrapper>

      <Modal
        ref={modalRef}
        component={Form}
        title={title}
        loading={loading}
        onClose={handleRequestGETData}
        maxWidth="sm"
      />

      <Modal
        ref={deleteRef}
        component={Confirmation}
        title="Continuar exclusão?"
        maxWidth="xs"
        loading={loading}
        actions={[
          {
            label: 'Cancelar',
            callback: () => cancellDelete(),
          },
          {
            label: 'Sim, continuar!',
            autoFocus: true,
            callback: () => confirmDelete(),
          },
        ]}
      />
    </>
  );
};

export default SimpleTable;

SimpleTable.propTypes = {
  heading: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  url: PropTypes.string.isRequired,
  onCreate: PropTypes.bool,
  onRead: PropTypes.bool,
  onUpdate: PropTypes.bool,
  onDelete: PropTypes.bool,
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      align: PropTypes.oneOf(['center', 'inherit', 'justify', 'left', 'right']),
      format: PropTypes.func,
    })
  ).isRequired,
  unique: PropTypes.arrayOf(PropTypes.string).isRequired,
  form: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  title: PropTypes.string,
  restProps: PropTypes.objectOf(PropTypes.any),
  query: PropTypes.objectOf(PropTypes.any),
};

SimpleTable.defaultProps = {
  heading: false,
  onCreate: true,
  onRead: true,
  onUpdate: true,
  onDelete: true,
  title: '',
  restProps: {},
  query: {},
};
